import React, { useState, useEffect, useContext } from "react";
import {
  Label,
  Form,
  FormGroup,
  Row,
  Col,
  InputGroup,
  Input,
} from "reactstrap";
import { useController, useForm } from "react-hook-form";
import {
  getCarTypes,
  showAlert,
} from "../../Services/Shared.service";
import { getTaxGroupList, createPackagesBulk } from "../../Services/Packages.service";
import CustomSelect from "../../CommonElements/CustomSelect";
import SharedDataContext from "../../_helper/SharedData";
import Select from "react-select";
import { Box, Typography, Tab, Tabs, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import PackageRateCreateForm from "./PackageRateCreateForm";
import CombinationTable from "./CombinationTable";
import DatePicker from "react-datepicker";
import { ArrowDown, ArrowUp } from "react-feather";
import { serviceTypeOptions, serviceTypeMap } from "../../Constant/serviceTypeConstants";
import { addMonths } from "date-fns";

function PackagesBulkCreate({ handleCloseModal, registerCloseHandler, corporateClientData }) {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = useForm({
    defaultValues: {
      packageName: "",
      description: "",
      city: "",
      serviceType: null,
      carTypes: [],
      terminals: [],
      overallTax: null,
      applicableFromDate: new Date(),
      applicableToDate: addMonths(new Date(), 1),
    },
  });

  const { cityData } = useContext(SharedDataContext);
  const [carTypeData, setCarTypeData] = useState([]);
  const [combinedTax, setCombinedTax] = useState([]);
  const [combinations, setCombinations] = useState([]);
  const [isAdvanced, setIsAdvanced] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [showCloseWarning, setShowCloseWarning] = useState(false);

  const terminals = [
    { value: "IGI-T3", label: "IGI-T3" },
    { value: "IGI-T1", label: "IGI-T1" },
  ];



  // Tax group fetch
  useEffect(() => {
    getTaxGroupList().then((result) => {
      if (result.status === 200) {
        setCombinedTax(generateTaxCombinationsForSelectBox(result.data));
      }
    });
  }, []);

  function generateTaxCombinationsForSelectBox(taxData) {
    let basezero = taxData.filter((t) => t.taxRate === 0);
    let basenonzero = taxData.filter((t) => t.taxRate !== 0);
    basezero.sort((a, b) => a.taxCategory.localeCompare(b.taxCategory));
    basenonzero.sort((a, b) => a.taxCategory.localeCompare(b.taxCategory));
    let combinationsArr = [];
    if (basenonzero.length >= 2) {
      combinationsArr.push({
        label: `Base ${basenonzero[0].taxRate}% Service ${basenonzero[1].taxRate}%`,
        value: { baseTax: basenonzero[0], serviceTax: basenonzero[1] },
      });
    }
    if (basezero.length >= 2) {
      combinationsArr.push({
        label: `Base ${basezero[0].taxRate}% Service ${basezero[1].taxRate}%`,
        value: { baseTax: basezero[0], serviceTax: basezero[1] },
      });
    }
    return combinationsArr;
  }

  // Form field controllers
  const cityField = useController({
    name: "city",
    control,
    rules: {
      required: { value: true, message: "City is Required" },
    },
  }).field;

  const serviceTypeField = useController({
    name: "serviceType",
    control,
    rules: {
      required: { value: true, message: "Service Type is Required" },
    },
  }).field;

  const carTypesField = useController({
    name: "carTypes",
    control,
    rules: {
      validate: (value) => (value && value.length > 0) || "At least one Car Type is required",
    },
  }).field;

  const terminalsField = useController({
    name: "terminals",
    control,
    rules: {
      validate: (value) => {
        const city = getValues("city");
        const serviceType = getValues("serviceType");
        if (city?.id === 1 && serviceType?.value === "A2C") {
          return (value && value.length > 0) || "At least one Terminal is required";
        }
        return true;
      },
    },
  }).field;

  const applicableFromDateField = useController({
    name: "applicableFromDate",
    control,
    rules: {
      required: { value: true, message: "Applicable From Date is Required" },
    },
  }).field;

  const applicableToDateField = useController({
    name: "applicableToDate",
    control,
    rules: {
      required: { value: true, message: "Applicable To Date is Required" },
      validate: (value) => {
        const from = getValues("applicableFromDate");
        if (!from || !value) return true;
        return value > from || "Applicable To must be greater than Applicable From";
      },
    },
  }).field;

  const overallTaxField = useController({
    name: "overallTax",
    control,
    rules: {
      validate: (value) => {
        if (!corporateClientData && !value) {
          return "Tax is Required";
        }
        return true;
      },
    },
  }).field;

  const watchCity = watch("city");
  const watchServiceType = watch("serviceType");
  const [draftRates, setDraftRates] = useState(null);
  const watchOverallTax = watch("overallTax");

  // Check if terminal multi-select should be shown
  const showTerminals = watchCity?.id === 1 && watchServiceType?.value === "A2C";

  // Fetch car types when city or service type changes
  useEffect(() => {
    const serviceCityId = watchCity?.id;
    if (!serviceCityId) {
      setCarTypeData([]);
      return;
    }
    let obj = {
      serviceCityId,
      travelType: watchServiceType?.value || "C2A",
    };
    getCarTypes(obj)
      .then((result) => {
        const data = result.data.map((element) => ({
          label: element.name,
          value: element,
        }));
        setCarTypeData(data);
      })
      .catch((err) => {
        showAlert("error", "Unable to fetch Car details for selected City");
      });
  }, [watchCity, watchServiceType]);

  // Clear terminals when showTerminals changes to false
  useEffect(() => {
    if (!showTerminals) {
      setValue("terminals", []);
    }
  }, [showTerminals, setValue]);

  const handleCityChange = (data) => {
    cityField.onChange(data.value);
    setValue("carTypes", []);
    setValue("terminals", []);
  };

  const handleServiceTypeChange = (data) => {
    serviceTypeField.onChange(data);
    setValue("carTypes", []);
    setValue("terminals", []);
  };

  const handleCarTypesChange = (selectedOptions) => {
    carTypesField.onChange(selectedOptions || []);
  };

  const handleTerminalsChange = (selectedOptions) => {
    terminalsField.onChange(selectedOptions || []);
  };

  const handleTaxChange = (selectedOption) => {
    overallTaxField.onChange(selectedOption);
  };

  // Add combinations - creates DTO matching PackagesDTO structure
  const addCombinations = (formValues) => {
    const { 
      packageName, 
      description,
      city, 
      serviceType, 
      carTypes, 
      terminals: selectedTerminals, 
      overallTax,
      applicableFromDate,
      applicableToDate,
    } = formValues;


    // C2A and C2C are isAirportExpress: true, A2C is false
    const isAirportExpress = serviceType.value === "C2A" || serviceType.value === "C2C" || serviceType.value === "All";
    
    // Helper to create base package object
    const createPackageDTO = (carType, terminalValue = null) => ({
      id: Date.now() + Math.random(),
      name: `${packageName}-${city.name}-${serviceType.value}-${carType.value.name}${terminalValue ? `-${terminalValue}` : ""}`,
      description: description || "",
      isActive: true,
      isKiosk: false,
      autoRollover: false,
      isAirportExpress,
      isAirportBooking: false,
      serviceCityId: city.id,
      serviceCityName: city.name,
      baseFareTaxId: corporateClientData ? corporateClientData.baseFareTaxId : (overallTax?.value?.baseTax?.taxGroupId || null),
      baseFareTaxName: corporateClientData ? (corporateClientData.baseFareTaxName || "") : (overallTax?.value?.baseTax?.name || ""),
      serviceTaxId: corporateClientData ? corporateClientData.serviceTaxId : (overallTax?.value?.serviceTax?.taxGroupId || null),
      serviceTaxName: corporateClientData ? (corporateClientData.serviceTaxName || "") : (overallTax?.value?.serviceTax?.name || ""),
      corporateClientId: corporateClientData ? corporateClientData.id : null,
      corporateClientName: corporateClientData ? corporateClientData.companyName : null,
      thirdPartyClientId: null,
      applicableFromDate: new Date(applicableFromDate),
      applicableToDate: new Date(applicableToDate),
      carTypeId: carType.value.id,
      carTypeName: carType.value.name,
      terminal: terminalValue,
      isDriverPayout: false,
      initialDistanceKm: null,
      perKmCharge: null,
      initialDistanceCharge: null,
      tollAmount: null,
      // Service type for packageRideType mapping in rate creation
      serviceType: serviceTypeMap[serviceType.value],
    });

    const newCombinations = [];

    if (showTerminals && selectedTerminals.length > 0) {
      // Delhi + A2C: CarType × Terminal combinations
      carTypes.forEach((carType) => {
        selectedTerminals.forEach((terminal) => {
          newCombinations.push(createPackageDTO(carType, terminal.value));
        });
      });
    } else {
      // Other cities or C2A: One entry per CarType
      carTypes.forEach((carType) => {
        newCombinations.push(createPackageDTO(carType));
      });
    }

    // Filter out duplicates (same city + carType + terminal + serviceType + package name)
    const filteredCombinations = newCombinations.filter((newPkg) => {
      const isDuplicate = combinations.some(
        (existing) =>
          existing.serviceCityId === newPkg.serviceCityId &&
          existing.carTypeId === newPkg.carTypeId &&
          existing.terminal === newPkg.terminal &&
          existing.isAirportExpress === newPkg.isAirportExpress &&
          existing.name === newPkg.name
      );
      if (isDuplicate) {
        showAlert("warning", `Package "${newPkg.name}" already exists`);
      }
      return !isDuplicate;
    });

    if (filteredCombinations.length > 0) {
      setCombinations((prev) => [...prev, ...filteredCombinations]);
    }
    
    // Reset only package name and car types for quick adding
    setValue("packageName", "");
    setValue("carTypes", []);
    setValue("terminals", []);
  };

  const removeCombination = (id) => {
    // Find index of item to remove
    const indexToRemove = combinations.findIndex(item => item.id === id);
    
    if (indexToRemove !== -1 && draftRates) {
      // Update draftRates: remove item at index, shift subsequent items down
      const newDraftRates = { ...draftRates };
      delete newDraftRates[indexToRemove];
      
      // Shift keys down
      Object.keys(newDraftRates).forEach(key => {
        const numKey = Number(key);
        if (numKey > indexToRemove) {
          newDraftRates[numKey - 1] = newDraftRates[numKey];
          delete newDraftRates[numKey];
        }
      });
      
      setDraftRates(newDraftRates);
    }
    setCombinations((prev) => prev.filter((item) => item.id !== id));
  };

  // Toggle isKiosk for a specific row
  const toggleIsKiosk = (id) => {
    setCombinations((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, isKiosk: !item.isKiosk } : item
      )
    );
  };

  // Toggle isActive for a specific row
  const toggleIsActive = (id) => {
    setCombinations((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, isActive: !item.isActive } : item
      )
    );
  };

  const handleProceed = () => {
    if (combinations.length === 0) {
      showAlert("warning", "Please add at least one combination");
      return;
    }
    setActiveTab(1); // Switch to Rate Creation tab
  };

  // Handler for PackageRateCreateForm onSubmit
  const handlePackagesBulkCreate = async (packagesDTO) => {
    const response = await createPackagesBulk(packagesDTO);
    if (response?.status === 201) {
      showAlert("success", `Created ${packagesDTO.length} package(s) with rates successfully!`);
    }
  };

  // Handle modal close with warning
  const handleCloseWithWarning = () => {
    // Show warning only if packages have been created
    if (combinations.length > 0) {
      setShowCloseWarning(true);
      return;
    }
    // No packages created, close directly
    if (handleCloseModal) handleCloseModal();
  };

  // Confirm close
  const confirmClose = () => {
    setShowCloseWarning(false);
    if (handleCloseModal) handleCloseModal();
  };

  // Register close warning handler with parent
  useEffect(() => {
    if (registerCloseHandler) {
      registerCloseHandler(handleCloseWithWarning);
    }
  }, [combinations.length]);

  return (
    <div>
      {/* Tab Header */}
      <Tabs
        value={activeTab}
        onChange={(e, newValue) => {
          if (newValue < activeTab) {
            setActiveTab(newValue);
            return;
          }
          if (activeTab === 0 && newValue > 0) {
            if (combinations.length === 0) {
              showAlert("warning", "Please add at least one package combination");
              return;
            }
          }
          setActiveTab(newValue);
        }}
        sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
      >
        <Tab label="1. Create Packages" />
        <Tab label="2. Create Rates" disabled={combinations.length === 0} />
      </Tabs>

      {/* TAB 1: Package Creation */}
      {activeTab === 0 && (
        <>
          <Form onSubmit={handleSubmit(addCombinations)}>
            <Row>
              {/* Package Name */}
              <FormGroup className="col-lg-4">
                <Label className="col-lg-12 col-form-label">Package Name</Label>
                <Col lg="12">
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Enter package name"
                    maxLength={15}
                    {...register("packageName", {
                      required: { value: true, message: "Package Name is required" },
                    })}
                  />
                  <span style={{ color: "red" }}>{errors?.packageName?.message}</span>
                </Col>
              </FormGroup>

              {/* City */}
              <FormGroup className="col-lg-4">
                <Label className="col-lg-12 col-form-label">City</Label>
                <Col lg="12">
                  <CustomSelect
                    className="service-select"
                    classNamePrefix="select"
                    isClearable={false}
                    isSearchable={false}
                    value={
                      watchCity?.id
                        ? cityData.find((element) => element?.value?.id === watchCity?.id)
                        : ""
                    }
                    onChange={handleCityChange}
                    placeholder="Select City"
                    options={corporateClientData ? cityData.filter(city => city.value.id === corporateClientData.serviceCityId) : cityData}
                  />
                  <span style={{ color: "red" }}>{errors?.city?.message}</span>
                </Col>
              </FormGroup>

              {/* Service Type */}
              <FormGroup className="col-lg-4">
                <Label className="col-lg-12 col-form-label">Service Type</Label>
                <Col lg="12">
                  <Select
                    value={watchServiceType}
                    onChange={handleServiceTypeChange}
                    placeholder="Select Service Type"
                    options={serviceTypeOptions}
                  />
                  <span style={{ color: "red" }}>{errors?.serviceType?.message}</span>
                </Col>
              </FormGroup>

              {/* Car Types - Multi Select */}
              <FormGroup className="col-lg-4">
                <Label className="col-lg-12 col-form-label">Car Types</Label>
                <Col lg="12">
                  <Select
                    isMulti
                    closeMenuOnSelect={false}
                    hideSelectedOptions={true}
                    noOptionsMessage={() => null}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    value={carTypesField.value}
                    onChange={handleCarTypesChange}
                    placeholder={watchCity?.id ? "Select Car Types" : "Select city first"}
                    options={carTypeData}
                    isDisabled={!watchCity?.id}
                    getOptionValue={(option) => option.value?.id || option.value}
                  />
                  <span style={{ color: "red" }}>{errors?.carTypes?.message}</span>
                </Col>
              </FormGroup>

              {/* Terminals - Multi Select (only for Delhi + A2C) */}
              {showTerminals && (
                <FormGroup className="col-lg-4">
                  <Label className="col-lg-12 col-form-label">Terminals</Label>
                  <Col lg="12">
                    <Select
                      isMulti
                      closeMenuOnSelect={false}
                      hideSelectedOptions={true}
                      noOptionsMessage={() => null}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      value={terminalsField.value}
                      onChange={handleTerminalsChange}
                      placeholder="Select Terminals"
                      options={terminals}
                      getOptionValue={(option) => option.value}
                    />
                    <span style={{ color: "red" }}>{errors?.terminals?.message}</span>
                  </Col>
                </FormGroup>
              )}

              {/* Tax */}
              {!corporateClientData && (
                <FormGroup className="col-lg-4">
                  <Label className="col-lg-12 col-form-label">Tax</Label>
                  <Col lg="12">
                    <Select
                      value={watchOverallTax}
                      onChange={handleTaxChange}
                      placeholder="Select Tax"
                      options={combinedTax}
                    />
                    <span style={{ color: "red" }}>{errors?.overallTax?.message}</span>
                  </Col>
                </FormGroup>
              )}
            </Row>

            {/* Advanced Configurations Toggle */}
            <Row>
              <div
                style={{
                  color: "#11BF5C",
                  textDecoration: "underline",
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  marginLeft: "12px",
                  marginTop: "10px",
                }}
                onClick={() => setIsAdvanced(!isAdvanced)}
              >
                Advanced Configurations{" "}
                {isAdvanced ? (
                  <ArrowUp style={{ width: "15px", height: "15px", marginLeft: "5px" }} />
                ) : (
                  <ArrowDown style={{ width: "15px", height: "15px", marginLeft: "5px" }} />
                )}
              </div>
            </Row>

            {/* Advanced Configurations Section */}
            {isAdvanced && (
              <Row style={{ marginTop: "15px" }}>
                <FormGroup className="col-lg-6">
                  <Label className="col-lg-12 col-form-label">Description</Label>
                  <Col lg="12">
                    <input
                      className="form-control"
                      type="text"
                      placeholder="Enter description"
                      {...register("description")}
                    />
                  </Col>
                </FormGroup>

                <FormGroup className="col-lg-6">
                  <Label className="col-lg-12 col-form-label">Available From</Label>
                  <Col lg="12">
                    <InputGroup className="date" data-target-input="nearest">
                      <DatePicker
                        className="form-control datetimepicker-input digits"
                        selected={applicableFromDateField.value}
                        showTimeSelect
                        showYearDropdown
                        showMonthDropdown
                        scrollableYearDropdown
                        onChange={applicableFromDateField.onChange}
                        dateFormat="MMMM d yyyy, h:mm:ss a"
                        onKeyDown={(e) => e.preventDefault()}
                      />
                    </InputGroup>
                    <span style={{ color: "red" }}>{errors?.applicableFromDate?.message}</span>
                  </Col>
                </FormGroup>

                <FormGroup className="col-lg-6">
                  <Label className="col-lg-12 col-form-label">Available To</Label>
                  <Col lg="12">
                    <InputGroup className="date" data-target-input="nearest">
                      <DatePicker
                        className="form-control datetimepicker-input digits"
                        selected={applicableToDateField.value}
                        onChange={applicableToDateField.onChange}
                        showTimeSelect
                        showYearDropdown
                        showMonthDropdown
                        scrollableYearDropdown
                        dateFormat="MMMM d yyyy, h:mm:ss a"
                        onKeyDown={(e) => e.preventDefault()}
                        minDate={applicableFromDateField.value}
                        minTime={
                          applicableToDateField.value &&
                          applicableFromDateField.value &&
                          applicableToDateField.value.toDateString() ===
                            applicableFromDateField.value.toDateString()
                            ? applicableFromDateField.value
                            : new Date(0, 0, 0, 0, 0, 0)
                        }
                        maxTime={new Date(0, 0, 0, 23, 59, 59)}
                      />
                    </InputGroup>
                    <span style={{ color: "red" }}>{errors?.applicableToDate?.message}</span>
                  </Col>
                </FormGroup>
              </Row>
            )}

            <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
              <button
                type="submit"
                className="btn"
                style={{
                  backgroundColor: "rgba(57, 181, 74, 1)",
                  color: "white",
                  border: "none",
                  padding: "10px 30px",
                  borderRadius: "6px",
                }}
              >
                Add
              </button>
            </div>
          </Form>

          {/* Combinations Table */}
          {combinations.length > 0 && (
            <Box sx={{ marginTop: "30px" }}>
              <Typography variant="h6" sx={{ marginBottom: "15px", fontWeight: 600 }}>
                Packages ({combinations.length})
              </Typography>
              <CombinationTable
                combinations={combinations}
                onToggleKiosk={toggleIsKiosk}
                onToggleActive={toggleIsActive}
                onRemove={removeCombination}
                showCity
                showServiceType
                showCarType
                showTerminal
              />

              <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
                <button
                  type="button"
                  className="btn"
                  onClick={handleProceed}
                  style={{
                    backgroundColor: "rgba(57, 181, 74, 1)",
                    color: "white",
                    border: "none",
                    padding: "10px 40px",
                    borderRadius: "6px",
                    fontSize: "16px",
                  }}
                >
                  Proceed to Rates →
                </button>
              </div>
            </Box>
          )}
        </>
      )}

      {/* TAB 2: Rate Creation */}
      {activeTab === 1 && (
        <PackageRateCreateForm
          packages={combinations}
          initialRates={draftRates}
          onRatesChange={setDraftRates}
          onSubmit={handlePackagesBulkCreate}
          onComplete={() => {
            if (handleCloseModal) handleCloseModal();
          }}
          onBack={() => setActiveTab(0)}
        />
      )}

      {/* Warning Dialog: Close confirmation */}
      <Dialog open={showCloseWarning} onClose={() => setShowCloseWarning(false)}>
        <DialogTitle>Confirm Close</DialogTitle>
        <DialogContent>
          <Typography>
            You have Unsaved Changes.
          </Typography>
          <Typography sx={{ mt: 2, fontWeight: 500 }}>
            Are you sure you want to leave?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCloseWarning(false)} sx={{ color: '#666' }}>
            Cancel
          </Button>
          <Button 
            onClick={confirmClose} 
            variant="contained" 
            sx={{ backgroundColor: '#dc3545', '&:hover': { backgroundColor: '#c82333' } }}
          >
            Leave
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default PackagesBulkCreate;
